import React from 'react';
import { Card, CardMedia, CardContent, Typography, Box, Avatar, IconButton, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { YouTubeVideo, YouTubeChannel } from '../../types/api.types';
import { formatViewCount, timeSince, formatVideoTitle, formatChannelTitle } from '../../utils/format';
import { getYouTubeVideoUrl, getYouTubeChannelUrl } from '../../services/channelService';
import { Link } from 'react-router-dom';

interface VideoTileProps {
  video: YouTubeVideo;
  channelMap?: Record<string, YouTubeChannel>;
}

export const VideoTile: React.FC<VideoTileProps> = ({ video, channelMap }) => {
  const channel = channelMap?.[video.channelId];
  const videoUrl =
    video.videoType === 'short' ? `https://www.youtube.com/shorts/${video.id}` : getYouTubeVideoUrl(video.id);

  const handleVideoClick = () => {
    window.open(videoUrl, '_blank', 'noopener,noreferrer');
  };

  const handleChannelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const channelUrl = channel?.customUrl
      ? `https://www.youtube.com/${channel.customUrl}`
      : getYouTubeChannelUrl(video.channelId);
    window.open(channelUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenVideo = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleVideoClick();
  };

  return (
    <Card
      data-cy="video-tile"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme => theme.shadows[8],
        },
      }}
    >
      {/* Video Thumbnail */}
      <Box sx={{ position: 'relative' }}>
        <Link to={videoUrl} target="_blank" rel="noopener noreferrer">
          <CardMedia
            component="img"
            height="180"
            image={video.thumbnailUrl || '/placeholder-video.jpg'}
            alt={video.title}
            sx={{
              objectFit: 'cover',
              backgroundColor: '#f0f0f0',
            }}
          />

          {/* Open in new tab button */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              borderRadius: '50%',
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              '.MuiCard-root:hover &': {
                opacity: 1,
              },
            }}
          >
            <Tooltip title="Open video">
              <IconButton size="small" onClick={handleOpenVideo} sx={{ color: 'white', p: 0.5 }}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Link>
      </Box>

      <CardContent
        sx={{
          flexGrow: 1,
          padding: '16px !important',
          '&:last-child': {
            paddingBottom: '16px !important',
          },
        }}
        onClick={handleVideoClick}
      >
        {/* Channel Info Row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Avatar
            src={channel?.thumbnailUrl}
            alt={channel?.title || 'Channel'}
            sx={{
              width: 36,
              height: 36,
              cursor: 'pointer',
              flexShrink: 0,
              mt: 0.25,
            }}
            onClick={handleChannelClick}
          />

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Video Title */}
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                mb: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
              title={video.title}
            >
              {formatVideoTitle(video.title, 65)}
            </Typography>

            {/* Channel Name */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
                display: 'block',
                mb: 0.25,
              }}
              onClick={handleChannelClick}
              title={channel?.title || 'Unknown Channel'}
            >
              {formatChannelTitle(channel?.title || 'Unknown Channel', 35)}
            </Typography>

            {/* Video Stats */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {video.viewCount !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  {formatViewCount(video.viewCount)}
                </Typography>
              )}

              {video.viewCount !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  •
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary">
                {timeSince(video.publishedAt)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
